import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useCurrency } from '@/hooks/useCurrency';
import {
  DollarSign,
  FileText,
  Tag,
  Store,
  StickyNote,
  Clock,
  Pencil,
  Trash2,
  Zap,
  Layers,
  CreditCard,
  FolderOpen,
  Coins,
  AlertCircle,
} from 'lucide-react';
import type { TransactionTemplate } from '@/lib/types';

interface TemplateDetailSheetProps {
  template: TransactionTemplate | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit: (template: TransactionTemplate) => void;
  onDelete: (id: string) => void;
  onQuickCreate: (template: TransactionTemplate) => void;
}

const typeConfig: Record<string, { badgeClass: string; heroBg: string }> = {
  expense: {
    badgeClass: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    heroBg: 'bg-red-50 dark:bg-red-950/30',
  },
  income: {
    badgeClass: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    heroBg: 'bg-green-50 dark:bg-green-950/30',
  },
};

function DetailRow({
  icon: Icon,
  label,
  children,
}: {
  icon: React.ElementType;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted/60">
        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-0.5">
          {label}
        </p>
        <div className="text-sm text-foreground">{children}</div>
      </div>
    </div>
  );
}

export default function TemplateDetailSheet({
  template,
  open,
  onOpenChange,
  onEdit,
  onDelete,
  onQuickCreate,
}: TemplateDetailSheetProps) {
  const { fmt, currency: userCurrency } = useCurrency();

  if (!template) return null;

  const tpl = template;
  const tType = tpl.type || 'expense';
  const config = typeConfig[tType] ?? typeConfig.expense;

  const accountName =
    typeof tpl.account === 'object' && tpl.account !== null
      ? tpl.account.name
      : null;

  const categoryName =
    typeof tpl.category === 'object' && tpl.category !== null
      ? tpl.category.name
      : null;

  const categoryIcon =
    typeof tpl.category === 'object' && tpl.category !== null
      ? tpl.category.icon
      : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-[calc(100vw-3rem)] sm:max-w-[480px] h-[100dvh] flex flex-col p-0 gap-0 overflow-hidden pb-[env(safe-area-inset-bottom)]"
        data-testid="template-detail-sheet"
      >
        {/* Hero */}
        <div className={`px-6 pt-8 pb-6 ${config.heroBg}`}>
          <SheetHeader className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/80 dark:bg-black/20 shadow-sm shrink-0 text-2xl leading-none">
                {tpl.emoji || tpl.name.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <SheetTitle className="text-left leading-tight break-words">
                  {tpl.name}
                </SheetTitle>
                <span
                  className={`mt-1 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize ${config.badgeClass}`}
                >
                  {tType}
                </span>
              </div>
            </div>

            {/* Amount */}
            {tpl.amount != null && (
              <div>
                <span className="text-3xl font-bold tabular-nums">
                  {fmt(tpl.amount)}
                </span>
                {tpl.currency && tpl.currency !== userCurrency && (
                  <Badge variant="outline" className="ml-2 text-xs">
                    {tpl.currency}
                  </Badge>
                )}
              </div>
            )}
          </SheetHeader>
        </div>

        {/* Detail rows */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {/* Type */}
          <DetailRow icon={Layers} label="Type">
            <Badge
              variant="outline"
              className={`capitalize ${config.badgeClass}`}
            >
              {tType}
            </Badge>
          </DetailRow>

          {/* Default Amount */}
          <DetailRow icon={DollarSign} label="Default Amount">
            {tpl.amount != null ? fmt(tpl.amount) : (
              <span className="text-muted-foreground">Not set</span>
            )}
          </DetailRow>

          {/* Description */}
          {tpl.description && (
            <DetailRow icon={FileText} label="Description">
              <span className="whitespace-pre-wrap break-words">{tpl.description}</span>
            </DetailRow>
          )}

          {/* Account */}
          <DetailRow icon={CreditCard} label="Account">
            {accountName || <span className="text-muted-foreground">Not set</span>}
          </DetailRow>

          {/* Category */}
          <DetailRow icon={FolderOpen} label="Category">
            {categoryName ? (
              <span>
                {categoryIcon && `${categoryIcon} `}
                {categoryName}
              </span>
            ) : (
              <span className="text-muted-foreground">Not set</span>
            )}
          </DetailRow>

          {/* Currency */}
          <DetailRow icon={Coins} label="Currency">
            {tpl.currency || userCurrency}
          </DetailRow>

          {/* Tags */}
          <DetailRow icon={Tag} label="Tags">
            {tpl.tags?.length ? (
              <div className="flex flex-wrap gap-1">
                {tpl.tags.map((tag) => (
                  <Badge key={tag} variant="secondary" className="text-xs">
                    {tag}
                  </Badge>
                ))}
              </div>
            ) : (
              <span className="text-muted-foreground">None</span>
            )}
          </DetailRow>

          {/* Merchant */}
          {tpl.merchant && (
            <DetailRow icon={Store} label="Merchant">
              {tpl.merchant}
            </DetailRow>
          )}

          {/* Notes */}
          {tpl.notes && (
            <DetailRow icon={StickyNote} label="Notes">
              <span className="whitespace-pre-wrap break-words">{tpl.notes}</span>
            </DetailRow>
          )}

          {/* Needs Review */}
          <DetailRow icon={AlertCircle} label="Needs Review">
            {tpl.needsReview ? (
              <Badge variant="outline" className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                Yes
              </Badge>
            ) : (
              <span className="text-muted-foreground">No</span>
            )}
          </DetailRow>

          <Separator />

          {/* Created */}
          <DetailRow icon={Clock} label="Created">
            {new Date(tpl.createdAt).toLocaleString(undefined, {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </DetailRow>
        </div>

        {/* Footer */}
        <SheetFooter className="sticky bottom-0 border-t bg-background/80 backdrop-blur-md px-4 sm:px-6 py-3 gap-2 flex-row flex-wrap">
          {/* Delete — far left */}
          <Button
            variant="outline"
            size="sm"
            className="min-h-[44px] mr-auto text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
            onClick={() => {
              onOpenChange(false);
              onDelete(tpl._id);
            }}
          >
            <Trash2 className="h-4 w-4 mr-1.5" />
            Delete
          </Button>

          {/* Use + Edit — right */}
          <Button
            variant="outline"
            size="sm"
            className="min-h-[44px]"
            onClick={() => {
              onOpenChange(false);
              onQuickCreate(tpl);
            }}
          >
            <Zap className="h-4 w-4 mr-1.5" />
            Use
          </Button>
          <Button
            size="sm"
            className="min-h-[44px]"
            onClick={() => {
              onOpenChange(false);
              onEdit(tpl);
            }}
          >
            <Pencil className="h-4 w-4 mr-1.5" />
            Edit
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
